import L from "leaflet";
import { api, get, post } from "./api";
import * as store from "./store";
import { syncSoon } from "./sync";
import { h, norm, parcelAt, plural, spColor, toast, uuid } from "./util";
import { I } from "./icons";
import { CUZK_METADATA, CUZK_TERMS, prefetch, tileUrls } from "./map";
import { KIND_LABEL, STAVY, STAV_LABEL, type Garden, type GardenShare, type Plant, type Stav } from "./types";
import { gardenPhotoSrc, photoSrc, type Ctx } from "./garden";
import { openForm } from "./plantform";
import { openParcels } from "./parcels";
import { confirmDeleteGarden, patchGardenLink, promptRenameGarden, uploadGardenPhoto } from "./gardens";
import { isDemo } from "./demo";

const sheetEl = (ctx: Ctx, id: string) => ctx.root.querySelector("#" + id) as HTMLElement;
const head = (title: string | Node, onClose: () => void, badge?: Node) =>
  h("div", { class: "sheet-head" }, badge ?? null, typeof title === "string" ? h("h2", {}, title) : title,
    h("button", { class: "close", "aria-label": "Zavřít", onclick: onClose }, "×"));

const canEditPlants = (ctx: Ctx) => ctx.garden.role !== "read";
const isGardenOwner = (ctx: Ctx) => !ctx.garden.role || ctx.garden.role === "owner";

export function openCard(ctx: Ctx, id: string) {
  const t = ctx.plants.get(id);
  if (!t) return;
  ctx.select(id);
  const close = () => { ctx.select(null); ctx.showSheet(null); };
  const badge = h("span", { class: "badge", style: "background:" + spColor(t.druh) }, String(t.cislo ?? "?"));
  const photos = ctx.photos.get(id) ?? [];
  const label = photos.find((p) => p.kind === "stitek");
  const main = photos.find((p) => p.kind !== "stitek") ?? label;
  const near = ctx.nearestTo(t.lat, t.lng, t.id);
  const nearDesc = near
    ? near.distText + " (" + (near.plant.cislo != null ? "#" + near.plant.cislo + " " : "") + ctx.label(near.plant) + ")"
    : null;
  const rows: [string, unknown][] = [
    ["Druh", t.druh],
    ["Odrůda", t.odruda],
    ["Výsadba", t.rok],
    ["Podnož", t.podnoz],
    ["Parcela", t.parcela],
    ["Nejbližší", nearDesc],
  ];
  const dl = h("dl", { class: "kv" });
  for (const [k, v] of rows) if (v) dl.append(h("dt", {}, k), h("dd", {}, String(v)));
  const stavCls = t.stav === "nemocny" || t.stav === "uschly" ? " bad" : t.stav === "slabsi" ? " warn" : "";
  const duplicate = () => {
    close();
    toast("Umísti křížek pro kopii rostliny");
    ctx.startPlacing("new", L.latLng(t.lat, t.lng), (nextLl) => openForm(ctx, null, nextLl, t));
  };
  const body = h("div", { class: "sheet-body" },
    h("div", { class: "card-top" }, main ? h("img", { src: photoSrc(main), alt: "Fotka rostliny", onclick: () => viewPhoto(main) }) : null, dl),
    h("div", { class: "summary" },
      h("span", { class: "pill" + stavCls }, STAV_LABEL[t.stav] ?? "Zdravý"),
      ctx.pending.has(id) ? h("span", { class: "pill warn" }, "Čeká na odeslání") : null,
      label ? h("span", { class: "pill" }, "má fotku štítku") : null),
    t.poznamka ? h("p", { class: "hint", style: "white-space:pre-wrap" }, t.poznamka) : null,
    photos.length > 1 ? h("div", { class: "photos" }, ...photos.filter((p) => p !== main).map((p) =>
      h("div", { class: "ph" }, h("img", { src: photoSrc(p), alt: KIND_LABEL[p.kind], loading: "lazy", onclick: () => viewPhoto(p) }), h("span", { class: "tag" }, KIND_LABEL[p.kind])))) : null,
    canEditPlants(ctx)
      ? h("div", { class: "btns even" },
          h("button", { class: "btn primary", onclick: () => openForm(ctx, id) }, "Upravit"),
          h("button", { class: "btn ghost", onclick: duplicate, html: I.copy + "<span>Duplikovat</span>" }))
      : null);
  sheetEl(ctx, "cardSheet").replaceChildren(head(ctx.label(t), close, badge), body);
  ctx.showSheet("cardSheet");
  requestAnimationFrame(() => {
    const pt = ctx.rot.containerToScreen(ctx.map.latLngToContainerPoint([t.lat, t.lng]));
    const visible = ctx.root.clientHeight - sheetEl(ctx, "cardSheet").offsetHeight;
    if (pt.y > visible - 40 || pt.y < 80) ctx.rot.panByScreen(0, pt.y - (visible + 60) / 2);
  });
}

function viewPhoto(p: Parameters<typeof photoSrc>[0]) {
  const ov = h("div", { style: "position:fixed;inset:0;z-index:2000;background:rgba(0,0,0,.92);display:flex;align-items:center;justify-content:center;padding:env(safe-area-inset-top,0) 0 env(safe-area-inset-bottom,0)", role: "dialog", "aria-label": "Fotka" },
    h("img", { src: photoSrc(p, "full"), alt: KIND_LABEL[p.kind], style: "max-width:100%;max-height:100%;object-fit:contain" }),
    h("button", { class: "close", style: "position:absolute;top:calc(12px + env(safe-area-inset-top,0px));right:12px", "aria-label": "Zavřít" }, "×"));
  ov.onclick = () => ov.remove();
  document.body.append(ov);
}

export function openList(ctx: Ctx) {
  const q = h("input", { id: "plantSearch", type: "search", placeholder: "druh, odrůda, číslo…", value: ctx.filter.q }) as HTMLInputElement;
  const speciesChips = h("div", { class: "chips", role: "group", "aria-label": "Filtr podle druhu" });
  const stavChips = h("div", { class: "chips", role: "group", "aria-label": "Filtr podle stavu" });
  const filterActions = h("div", { class: "btns even", hidden: true });
  const ul = h("ul", { class: "tl" });
  const importErr = h("p", { class: "err", role: "alert", hidden: true });
  const fileIn = h("input", { type: "file", accept: ".csv,text/csv,text/plain", hidden: true }) as HTMLInputElement;
  const titleEl = h("h2", {});
  const editable = canEditPlants(ctx);

  const draw = () => {
    const all = [...ctx.plants.values()].sort((a, b) => (a.cislo ?? 0) - (b.cislo ?? 0));
    const counts = new Map<string, number>();
    const stavCounts = new Map<Stav, number>();
    for (const t of all) {
      const sp = t.druh || "Neurčeno";
      counts.set(sp, (counts.get(sp) ?? 0) + 1);
      stavCounts.set(t.stav, (stavCounts.get(t.stav) ?? 0) + 1);
    }

    speciesChips.replaceChildren(
      h("button", {
        type: "button",
        class: "chip",
        "aria-pressed": String(ctx.filter.druh === ""),
        onclick: () => { ctx.filter.druh = ""; ctx.applyFilter(); draw(); },
      }, "Všechny druhy (" + all.length + ")"),
      ...[...counts].sort((a, b) => b[1] - a[1]).map(([k, n]) =>
        h("button", {
          type: "button",
          class: "chip",
          "aria-pressed": String(ctx.filter.druh === k),
          onclick: () => { ctx.filter.druh = ctx.filter.druh === k ? "" : k; ctx.applyFilter(); draw(); },
        }, k + " " + n)),
    );

    stavChips.replaceChildren(
      h("button", {
        type: "button",
        class: "chip",
        "aria-pressed": String(ctx.filter.stav === ""),
        onclick: () => { ctx.filter.stav = ""; ctx.applyFilter(); draw(); },
      }, "Všechny stavy"),
      ...STAVY.filter(([k]) => (stavCounts.get(k) ?? 0) > 0 || ctx.filter.stav === k).map(([k, l]) =>
        h("button", {
          type: "button",
          class: "chip",
          "aria-pressed": String(ctx.filter.stav === k),
          onclick: () => { ctx.filter.stav = ctx.filter.stav === k ? "" : k; ctx.applyFilter(); draw(); },
        }, l + " " + (stavCounts.get(k) ?? 0))),
    );

    const shown = all.filter((t) => ctx.matchesFilter(t));
    const hasFilter = ctx.hasActiveFilter();
    titleEl.textContent = hasFilter
      ? "Filtr: " + shown.length + " z " + all.length + " " + plural(all.length, "rostliny", "rostlin", "rostlin")
      : all.length + " " + plural(all.length, "rostlina", "rostliny", "rostlin");

    filterActions.hidden = !hasFilter;
    if (hasFilter) {
      filterActions.replaceChildren(
        h("button", {
          type: "button",
          class: "btn primary",
          onclick: () => ctx.showSheet(null),
          html: I.filter + "<span>Zobrazit na mapě (" + shown.length + ")</span>",
        }),
        h("button", {
          type: "button",
          class: "btn ghost",
          onclick: () => {
            ctx.filter = { q: "", druh: "", stav: "" };
            q.value = "";
            ctx.applyFilter();
            draw();
          },
        }, "Zrušit filtr"),
      );
    }

    ul.replaceChildren(...shown.map((t) => {
      const n = (ctx.photos.get(t.id) ?? []).length;
      const info = [t.rok ? "výsadba " + t.rok : "", t.podnoz, t.stav !== "zdravy" ? STAV_LABEL[t.stav] : "", n ? n + " foto" : "", ctx.pending.has(t.id) ? "čeká na odeslání" : ""].filter(Boolean).join(" · ");
      return h("li", {}, h("button", { onclick: () => { ctx.map.setView([t.lat, t.lng], Math.max(ctx.map.getZoom(), ctx.garden.mode === "photo" ? 4.5 : 20)); openCard(ctx, t.id); } },
        h("span", { class: "badge", style: "background:" + spColor(t.druh) }, String(t.cislo ?? "?")),
        h("span", { class: "t" }, h("b", {}, ctx.label(t)), h("small", {}, info || "bez dalších údajů"))));
    }));
    if (!shown.length) ul.append(h("li", { class: "empty" }, all.length ? "Filtru neodpovídá žádná rostlina." : "Zatím žádné rostliny."));
  };

  fileIn.onchange = async () => {
    const f = fileIn.files?.[0];
    fileIn.value = "";
    if (!f) return;
    importErr.hidden = true;
    importErr.textContent = "";
    try {
      const res = await importCsv(ctx, await f.text());
      draw();
      toast("Import hotov: " + res.updated + " upraveno, " + res.added + " přidáno");
    } catch (e) {
      importErr.textContent = e instanceof Error ? e.message : "Import se nezdařil.";
      importErr.hidden = false;
    }
  };

  q.addEventListener("input", () => {
    ctx.filter.q = q.value;
    ctx.applyFilter();
    draw();
  });
  sheetEl(ctx, "listSheet").replaceChildren(
    head(titleEl, () => ctx.showSheet(null)),
    h("div", { class: "sheet-body" },
      h("label", { class: "field" }, h("span", {}, "Hledat a filtrovat mapu"), q),
      speciesChips,
      stavChips,
      filterActions,
      importErr,
      ul,
      fileIn,
      h("div", { class: "btns even" },
        h("button", { type: "button", class: "btn ghost", onclick: () => exportCsv(ctx), html: I.download + "<span>Exportovat (CSV)</span>" }),
        editable ? h("button", { type: "button", class: "btn ghost", onclick: () => fileIn.click(), html: I.upload + "<span>Importovat (CSV)</span>" }) : null)));
  draw();
  ctx.showSheet("listSheet");
}

const CSV_COLS: (keyof Plant)[] = ["cislo", "druh", "odruda", "rok", "podnoz", "stav", "parcela", "lat", "lng", "poznamka", "id"];

function exportCsv(ctx: Ctx) {
  const cell = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[";,\n\r]/.test(s) ? "\"" + s.replace(/"/g, "\"\"") + "\"" : s;
  };
  const rows = [...ctx.plants.values()]
    .sort((a, b) => (a.cislo ?? 0) - (b.cislo ?? 0))
    .map((t) => CSV_COLS.map((c) => cell(t[c])).join(";"));
  const blob = new Blob(["\ufeff" + CSV_COLS.join(";") + "\n" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const a = h("a", { href: URL.createObjectURL(blob), download: "rostliny-" + (norm(ctx.garden.name).replace(/[^a-z0-9]+/g, "-") || "zahrada") + ".csv" });
  document.body.append(a); a.click(); a.remove();
}

const HEADER_MAP: Record<string, keyof Plant> = {
  cislo: "cislo",
  "cislo rostliny": "cislo",
  druh: "druh",
  odruda: "odruda",
  rok: "rok",
  "rok vysadby": "rok",
  vysadba: "rok",
  podnoz: "podnoz",
  stav: "stav",
  parcela: "parcela",
  lat: "lat",
  lng: "lng",
  lon: "lng",
  poznamka: "poznamka",
  id: "id",
};

const VALID_STAVY = new Set<string>(STAVY.map(([k]) => k));
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function parseCsvRows(text: string): string[][] {
  const clean = text.replace(/^\ufeff/, "");
  let firstLine = "";
  let inQ = false;
  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i];
    if (ch === "\"") inQ = !inQ;
    else if (!inQ && (ch === "\n" || ch === "\r")) break;
    else if (!inQ) firstLine += ch;
  }
  const semiCount = (firstLine.match(/;/g) ?? []).length;
  const commaCount = (firstLine.match(/,/g) ?? []).length;
  const tabCount = (firstLine.match(/\t/g) ?? []).length;
  const sep = semiCount >= commaCount && semiCount >= tabCount ? ";" : tabCount > commaCount ? "\t" : ",";

  const rows: string[][] = [];
  let row: string[] = [];
  let cur = "";
  let q = false;
  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i];
    if (q) {
      if (ch === "\"") {
        if (clean[i + 1] === "\"") { cur += "\""; i++; }
        else q = false;
      } else {
        cur += ch;
      }
    } else {
      if (ch === "\"") q = true;
      else if (ch === sep) { row.push(cur); cur = ""; }
      else if (ch === "\r" || ch === "\n") {
        if (ch === "\r" && clean[i + 1] === "\n") i++;
        row.push(cur); cur = "";
        if (row.length > 1 || row[0].trim() !== "") rows.push(row);
        row = [];
      } else {
        cur += ch;
      }
    }
  }
  if (q) throw new Error("Neuzavřené uvozovky v CSV souboru.");
  if (cur !== "" || row.length > 0) {
    row.push(cur);
    if (row.length > 1 || row[0].trim() !== "") rows.push(row);
  }
  return rows;
}

async function importCsv(ctx: Ctx, text: string): Promise<{ added: number; updated: number }> {
  if (!canEditPlants(ctx)) throw new Error("Tato zahrada je s tebou sdílená jen pro čtení.");
  const rows = parseCsvRows(text);
  if (!rows.length) throw new Error("Soubor je prázdný.");

  const rawHeaders = rows[0];
  const cols: (keyof Plant)[] = [];
  const seenCols = new Set<keyof Plant>();
  for (const rh of rawHeaders) {
    const key = norm(rh).trim();
    const mapped = HEADER_MAP[key];
    if (!mapped) throw new Error("Neočekávaný sloupec v tabulce: „" + rh.trim() + "“.");
    if (seenCols.has(mapped)) throw new Error("Duplicitní sloupec v tabulce: „" + rh.trim() + "“.");
    seenCols.add(mapped);
    cols.push(mapped);
  }

  if (rows.length < 2) throw new Error("Tabulka neobsahuje žádné řádky s rostlinami.");

  const now = new Date().toISOString();
  const toSave: { doc: Plant; isNew: boolean }[] = [];
  const claimedIds = new Set<string>();
  let nextAutoNum = ctx.nextNumber();

  for (let rIdx = 1; rIdx < rows.length; rIdx++) {
    const r = rows[rIdx];
    const lineNum = rIdx + 1;
    if (r.length > cols.length) {
      throw new Error("Řádek " + lineNum + ": obsahuje více hodnot (" + r.length + ") než hlavička (" + cols.length + ").");
    }
    const rec: Partial<Record<keyof Plant, string>> = {};
    for (let cIdx = 0; cIdx < cols.length; cIdx++) {
      rec[cols[cIdx]] = (r[cIdx] ?? "").trim();
    }

    let existing: Plant | undefined;
    const rawId = rec.id ?? "";
    if (rawId) {
      if (!UUID_RE.test(rawId)) throw new Error("Řádek " + lineNum + ": neplatné ID „" + rawId + "“.");
      if (claimedIds.has(rawId)) throw new Error("Řádek " + lineNum + ": duplicitní ID „" + rawId + "“ v souboru.");
      existing = ctx.plants.get(rawId);
    }

    let cislo: number | null = null;
    if (rec.cislo !== undefined && rec.cislo !== "") {
      const n = Number(rec.cislo);
      if (!Number.isInteger(n) || n < 0 || n > 100000) {
        throw new Error("Řádek " + lineNum + ": neplatné číslo rostliny „" + rec.cislo + "“.");
      }
      cislo = n;
    }

    if (!existing && !rawId && cislo !== null) {
      for (const p of ctx.plants.values()) {
        if (p.cislo === cislo && !claimedIds.has(p.id)) {
          existing = p;
          break;
        }
      }
    }

    const id = existing ? existing.id : (rawId || uuid());
    if (claimedIds.has(id)) throw new Error("Řádek " + lineNum + ": rostlina s číslem " + cislo + " je v tabulce vícekrát.");
    claimedIds.add(id);

    let rok: number | null = existing?.rok ?? null;
    if (rec.rok !== undefined) {
      if (rec.rok === "") rok = null;
      else {
        const y = Number(rec.rok);
        if (!Number.isInteger(y) || y < 1800 || y > 2200) {
          throw new Error("Řádek " + lineNum + ": neplatný rok výsadby „" + rec.rok + "“ (povoleno 1800–2200).");
        }
        rok = y;
      }
    }

    let stav: Stav = existing?.stav ?? "zdravy";
    if (rec.stav !== undefined && rec.stav !== "") {
      const stNorm = norm(rec.stav).trim();
      if (!VALID_STAVY.has(stNorm)) {
        throw new Error("Řádek " + lineNum + ": neznámý stav „" + rec.stav + "“ (povoleno: zdravý, slabší, nemocný, uschlý, plánovaný).");
      }
      stav = stNorm as Stav;
    }

    let lat = existing?.lat ?? NaN;
    let lng = existing?.lng ?? NaN;
    if (rec.lat !== undefined && rec.lat !== "") lat = Number(rec.lat.replace(",", "."));
    if (rec.lng !== undefined && rec.lng !== "") lng = Number(rec.lng.replace(",", "."));
    if (!Number.isFinite(lat) || lat < -90 || lat > 90 || !Number.isFinite(lng) || lng < -180 || lng > 180) {
      throw new Error("Řádek " + lineNum + ": chybí nebo jsou neplatné souřadnice (lat, lng).");
    }

    const druh = rec.druh !== undefined ? rec.druh : (existing?.druh ?? "");
    const odruda = rec.odruda !== undefined ? rec.odruda : (existing?.odruda ?? "");
    const podnoz = rec.podnoz !== undefined ? rec.podnoz : (existing?.podnoz ?? "");
    const poznamka = rec.poznamka !== undefined ? rec.poznamka : (existing?.poznamka ?? "");
    const rawParcela = rec.parcela !== undefined ? rec.parcela : (existing?.parcela ?? "");

    if (druh.length > 80) throw new Error("Řádek " + lineNum + ": druh je příliš dlouhý (max 80 znaků).");
    if (odruda.length > 120) throw new Error("Řádek " + lineNum + ": odrůda je příliš dlouhá (max 120 znaků).");
    if (podnoz.length > 80) throw new Error("Řádek " + lineNum + ": podnož je příliš dlouhá (max 80 znaků).");
    if (rawParcela.length > 40) throw new Error("Řádek " + lineNum + ": parcela je příliš dlouhá (max 40 znaků).");
    if (poznamka.length > 4000) throw new Error("Řádek " + lineNum + ": poznámka je příliš dlouhá (max 4000 znaků).");

    const finalCislo = cislo !== null ? cislo : (existing?.cislo ?? nextAutoNum++);
    if (finalCislo >= nextAutoNum) nextAutoNum = finalCislo + 1;

    const detectedParcel = parcelAt(ctx.garden.parcels, lat, lng)?.cislo;
    const parcela = detectedParcel ?? rawParcela;

    const doc: Plant = {
      id,
      garden_id: ctx.garden.id,
      cislo: finalCislo,
      druh,
      odruda,
      rok,
      podnoz,
      stav,
      poznamka,
      lat: +lat.toFixed(8),
      lng: +lng.toFixed(8),
      parcela,
      urceni: existing?.urceni ?? null,
      updated_at: now,
    };
    toSave.push({ doc, isNew: !existing });
  }

  let added = 0, updated = 0;
  for (const item of toSave) {
    await store.putPlant(item.doc);
    await store.queue({ type: "plant", id: item.doc.id, garden_id: item.doc.garden_id });
    if (item.isNew) added++; else updated++;
  }
  await ctx.reload();
  void syncSoon();
  return { added, updated };
}

export function openLayers(ctx: Ctx) {
  const isPhoto = ctx.garden.mode === "photo";
  const knOn = ctx.map.hasLayer(ctx.kn), plantsOn = ctx.map.hasLayer(ctx.plantLayer);
  const toggle = (label: string, checked: boolean, fn: (on: boolean) => void) => {
    const input = h("input", { type: "checkbox", checked }) as HTMLInputElement;
    input.onchange = () => fn(input.checked);
    return h("label", { class: "toggle" }, label, input);
  };
  if (isPhoto) {
    sheetEl(ctx, "layerSheet").replaceChildren(head("Zobrazení", () => ctx.showSheet(null)), h("div", { class: "sheet-body" },
      toggle("Rostliny", plantsOn, (on) => (on ? ctx.plantLayer.addTo(ctx.map) : ctx.plantLayer.remove())),
      toggle("Čísla u rostlin", ctx.showNumbers, (on) => { ctx.showNumbers = on; try { localStorage.setItem("zahrada:nums", on ? "1" : "0"); } catch {} ; void ctx.reload(); }),
      toggle("Vzdálenost k nejbližší rostlině", ctx.showNearestDist, (on) => ctx.setShowNearestDist(on)),
      h("p", { class: "hint" }, "Tato zahrada používá jako podklad vlastní fotku (bez katastrální mapy a GPS okolí).")));
    ctx.showSheet("layerSheet");
    return;
  }
  sheetEl(ctx, "layerSheet").replaceChildren(head("Vrstvy", () => ctx.showSheet(null)), h("div", { class: "sheet-body" },
    toggle("Katastrální mapa (hranice a čísla parcel)", knOn, (on) => { if (on) ctx.kn.addTo(ctx.map); else ctx.kn.remove(); try { localStorage.setItem("zahrada:kn", on ? "1" : "0"); } catch {} }),
    toggle("Hranice mé zahrady", ctx.map.hasLayer(ctx.parcelLayer), (on) => (on ? ctx.parcelLayer.addTo(ctx.map) : ctx.parcelLayer.remove())),
    toggle("Rostliny", plantsOn, (on) => (on ? ctx.plantLayer.addTo(ctx.map) : ctx.plantLayer.remove())),
    toggle("Čísla u rostlin", ctx.showNumbers, (on) => { ctx.showNumbers = on; try { localStorage.setItem("zahrada:nums", on ? "1" : "0"); } catch {} ; void ctx.reload(); }),
    toggle("Vzdálenost k nejbližší rostlině (v m)", ctx.showNearestDist, (on) => ctx.setShowNearestDist(on)),
    ctx.subgardens.length ? toggle("Značky záhonků na mapě", ctx.map.hasLayer(ctx.subgardenLayer), (on) => (on ? ctx.subgardenLayer.addTo(ctx.map) : ctx.subgardenLayer.remove())) : null,
    h("p", { class: "hint" }, "Katastrální mapa se zobrazuje od většího přiblížení."),
    h("div", { class: "source" },
      h("b", {}, "Zdroj dat"),
      h("p", { class: "hint" }, "Ortofoto, katastrální mapa a hranice parcel: ",
        h("a", { href: CUZK_TERMS, target: "_blank", rel: "noopener" }, "ČÚZK – on-line"), " (podmínky užití)."),
      h("p", { class: "hint" }, "Metadata služeb: ", ...CUZK_METADATA.flatMap(([name, url], i) => [
        i ? " · " : "", h("a", { href: url, target: "_blank", rel: "noopener" }, name)])))));
  ctx.showSheet("layerSheet");
}

export function openMenu(ctx: Ctx) {
  const item = (icon: string, label: string, fn: () => void, cls = "") => h("button", { class: cls, onclick: fn, html: icon + "<span>" + label + "</span>" });
  const owner = isGardenOwner(ctx);
  const isPhoto = ctx.garden.mode === "photo";
  const askRename = () => {
    promptRenameGarden(ctx.garden, (updated) => {
      ctx.setGardenName(updated.name);
      openMenu(ctx);
    });
  };
  const askDelete = () => {
    ctx.showSheet(null);
    confirmDeleteGarden(ctx.garden, ctx.plants.size, () => {
      (ctx as any).cleanup();
      ctx.nav.back();
    });
  };
  const subLabel = "Záhonky podle fotky" + (ctx.subgardens.length ? " (" + ctx.subgardens.length + ")" : "");
  const sharedHint = !owner
    ? h("p", { class: "hint", style: "padding-bottom:6px" },
        "Sdílená zahrada" + (ctx.garden.owner_email ? " od " + ctx.garden.owner_email : "") +
        " (" + (ctx.garden.role === "read" ? "režim jen pro čtení" : "můžeš upravovat rostliny") + ").")
    : null;
  sheetEl(ctx, "menuSheet").replaceChildren(head(ctx.garden.name, () => ctx.showSheet(null)), h("div", { class: "sheet-body" },
    sharedHint,
    isDemo() ? h("div", { class: "menu" },
      item(I.edit, "Přejmenovat zahradu", askRename),
      item(I.users, "Sdílení zahrady", () => openShare(ctx)),
      isPhoto ? item(I.image, "Fotka a propojení záhonku", () => void openGardenPhoto(ctx)) : item(I.parcel, "Parcely zahrady", () => openParcels(ctx)),
      !isPhoto ? item(I.grid, subLabel, () => void openSubgardens(ctx)) : null,
      ctx.parentGarden ? item(I.back, "Přejít do zahrady: " + ctx.parentGarden.name, () => { const pg = ctx.parentGarden!; (ctx as any).cleanup(); ctx.nav.openGarden(pg); }) : null,
      item(I.list, "Filtrovat / exportovat rostliny (CSV)", () => openList(ctx)),
      item(I.back, "Moje zahrady (ukázka)", () => { (ctx as any).cleanup(); ctx.nav.back(); }),
      item(I.trash, "Smazat zahradu", askDelete, "danger-item"),
      item(I.logout, "Ukončit ukázku", () => location.reload()),
      h("p", { class: "hint", style: "padding-top:10px" }, "V ukázce se nic neukládá. Po zavření nebo obnovení stránky vše zmizí. Pro skutečné použití si založ účet.")) :
    h("div", { class: "menu" },
      owner ? item(I.edit, "Přejmenovat zahradu", askRename) : null,
      owner ? item(I.users, "Sdílení zahrady", () => openShare(ctx)) : null,
      owner ? (isPhoto ? item(I.image, "Fotka a propojení záhonku", () => void openGardenPhoto(ctx)) : item(I.parcel, "Parcely zahrady", () => openParcels(ctx))) : null,
      !isPhoto ? item(I.grid, subLabel, () => void openSubgardens(ctx)) : null,
      ctx.parentGarden ? item(I.back, "Přejít do zahrady: " + ctx.parentGarden.name, () => { const pg = ctx.parentGarden!; (ctx as any).cleanup(); ctx.nav.openGarden(pg); }) : null,
      item(I.list, canEditPlants(ctx) ? "Filtrovat / export a import (CSV)" : "Seznam, filtr a export (CSV)", () => openList(ctx)),
      !isPhoto ? item(I.download, "Uložit mapu pro offline", () => openOffline(ctx)) : null,
      item(I.back, "Moje zahrady", () => { (ctx as any).cleanup(); ctx.nav.back(); }),
      item(I.trash, owner ? "Smazat zahradu" : "Odebrat sdílenou zahradu", askDelete, "danger-item"),
      item(I.logout, "Odhlásit (" + ctx.me.email + ")", () => ctx.nav.logout()))));
  ctx.showSheet("menuSheet");
}

export async function openGardenPhoto(ctx: Ctx) {
  const sheet = sheetEl(ctx, "photoSheet");
  const allGardens = await store.gardens();
  const mapGardens = allGardens.filter((g) => g.mode !== "photo" && g.id !== ctx.garden.id && g.role !== "read");

  const fileCam = h("input", { type: "file", accept: "image/*", capture: "environment", hidden: true }) as HTMLInputElement;
  const fileGal = h("input", { type: "file", accept: "image/*", hidden: true }) as HTMLInputElement;
  const errEl = h("p", { class: "err", role: "alert", hidden: true });
  const statusEl = h("p", { class: "hint" }, "Zde můžeš vyměnit podkladovou fotku záhonku nebo části zahrady. Pozice stávajících rostlin zůstane zachovaná.");
  const previewImg = h("img", { src: gardenPhotoSrc(ctx.garden), alt: "Podkladová fotka zahrady" }) as HTMLImageElement;

  const parentSelect = h("select", {},
    h("option", { value: "", selected: !ctx.garden.parent_id }, "Samostatný záhonek / zahrada (bez propojení)"),
    ...mapGardens.map((mg) => h("option", { value: mg.id, selected: ctx.garden.parent_id === mg.id }, "V zahradě: " + mg.name)),
  ) as HTMLSelectElement;

  parentSelect.onchange = async () => {
    const nextParent = parentSelect.value || null;
    try {
      const updated = await patchGardenLink(
        ctx.garden,
        nextParent,
        nextParent === ctx.garden.parent_id ? (ctx.garden.parent_lat ?? null) : null,
        nextParent === ctx.garden.parent_id ? (ctx.garden.parent_lng ?? null) : null,
      );
      ctx.garden = updated;
      await ctx.drawSubgardens();
      toast(nextParent ? "Záhonek propojen s hlavní zahradou" : "Záhonek je nyní samostatný");
    } catch (e) {
      errEl.textContent = e instanceof Error ? e.message : "Změna propojení se nezdařila.";
      errEl.hidden = false;
    }
  };

  const onPick = async (ev: Event) => {
    const input = ev.target as HTMLInputElement;
    const f = input.files?.[0];
    input.value = "";
    if (!f) return;
    errEl.hidden = true;
    statusEl.textContent = "Nahrávám novou fotku…";
    try {
      const updated = await uploadGardenPhoto(ctx.garden, f);
      ctx.updateGardenPhoto(updated);
      previewImg.src = gardenPhotoSrc(updated);
      statusEl.textContent = "Fotka podkladu byla aktualizována.";
      toast("Fotka zahrady aktualizována");
    } catch (e) {
      errEl.textContent = e instanceof Error ? e.message : "Nahrání fotky se nezdařilo.";
      errEl.hidden = false;
      statusEl.textContent = "";
    }
  };
  fileCam.onchange = onPick;
  fileGal.onchange = onPick;

  sheet.replaceChildren(
    head("Fotka a propojení záhonku", () => ctx.showSheet(null)),
    h("div", { class: "sheet-body" },
      h("div", { class: "photo-preview" }, previewImg),
      statusEl,
      errEl,
      h("div", { class: "btns even" },
        h("button", { type: "button", class: "btn primary", onclick: () => fileCam.click(), html: I.camera + "<span>Vyfotit novou</span>" }),
        h("button", { type: "button", class: "btn ghost", onclick: () => fileGal.click(), html: I.image + "<span>Vybrat z galerie</span>" })),
      mapGardens.length
        ? h("label", { class: "field", style: "padding-top:6px" },
            h("span", {}, "Propojení s hlavní zahradou"),
            parentSelect,
            h("small", { class: "hint" }, "Propojený záhonek lze umístit jako značku na mapu hlavní zahrady, nebo ho nechat samostatně."))
        : null,
      fileCam,
      fileGal));
  ctx.showSheet("photoSheet");
}

export async function openSubgardens(ctx: Ctx) {
  const sheet = sheetEl(ctx, "subgardensSheet");
  const editable = canEditPlants(ctx);
  const allGardens = await store.gardens();
  const linked = allGardens.filter((g) => g.parent_id === ctx.garden.id);
  const standalonePhotos = allGardens.filter((g) => g.mode === "photo" && !g.parent_id && (!g.role || g.role === "owner"));

  const listEl = h("ul", { class: "tl" });
  if (!linked.length) {
    listEl.append(h("li", { class: "empty" }, "V této zahradě zatím není propojený žádný záhonek podle fotky."));
  }
  for (const sub of linked) {
    const count = (await store.plantsOf(sub.id)).length;
    const hasPos = sub.parent_lat != null && sub.parent_lng != null;
    const openSub = () => {
      (ctx as any).cleanup();
      ctx.nav.openGarden(sub);
    };
    const placeSub = () => {
      const start = hasPos ? L.latLng(sub.parent_lat!, sub.parent_lng!) : null;
      toast("Umísti křížek na místo, kde leží záhonek „" + sub.name + "“");
      ctx.startPlacing("move", start, async (ll) => {
        try {
          await patchGardenLink(sub, ctx.garden.id, +ll.lat.toFixed(7), +ll.lng.toFixed(7));
          await ctx.drawSubgardens();
          toast("Poloha záhonku „" + sub.name + "“ uložena");
          void openSubgardens(ctx);
        } catch (e) {
          toast(e instanceof Error ? e.message : "Uložení polohy se nezdařilo.");
        }
      }, () => void openSubgardens(ctx));
    };
    const unlinkSub = async () => {
      try {
        await patchGardenLink(sub, null, null, null);
        await ctx.drawSubgardens();
        toast("Záhonek „" + sub.name + "“ odpojen (zůstává jako samostatný)");
        void openSubgardens(ctx);
      } catch (e) {
        toast(e instanceof Error ? e.message : "Odpojení se nezdařilo.");
      }
    };

    listEl.append(h("li", { class: "prow" },
      h("button", {
        type: "button",
        style: "flex:1;min-width:0;text-align:left;border:0;background:transparent;padding:4px 0;display:flex;align-items:center;gap:10px",
        onclick: openSub,
      },
        h("span", { class: "badge", style: "background:var(--accent)" }, sub.name.slice(0, 1).toUpperCase()),
        h("span", { class: "t" },
          h("b", {}, sub.name),
          h("small", { class: "hint" }, count + " " + plural(count, "rostlina", "rostliny", "rostlin") + " · " + (hasPos ? "umístěn na mapě" : "zatím neumístěn na mapě")))),
      editable ? h("button", {
        type: "button",
        class: "btn ghost icon-only",
        title: hasPos ? "Posunout značku na mapě" : "Umístit na mapu zahrady",
        onclick: placeSub,
        html: I.pin,
      }) : null,
      editable ? h("button", {
        type: "button",
        class: "btn ghost icon-only",
        title: "Odpojit záhonek (ponechat samostatně)",
        onclick: unlinkSub,
        html: I.trash,
      }) : null));
  }

  // Section to link an existing standalone photo garden
  const linkExistingBox = standalonePhotos.length && editable
    ? h("div", { class: "field", style: "padding-top:8px" },
        h("span", {}, "Propojit existující samostatný záhonek"),
        h("ul", { class: "tl" }, ...standalonePhotos.map((sp) =>
          h("li", { class: "prow" },
            h("span", { class: "t" }, h("b", {}, sp.name), h("small", { class: "hint" }, "samostatný záhonek podle fotky")),
            h("button", {
              type: "button",
              class: "chip",
              onclick: () => {
                toast("Umísti křížek na místo záhonku „" + sp.name + "“");
                ctx.startPlacing("new", null, async (ll) => {
                  try {
                    await patchGardenLink(sp, ctx.garden.id, +ll.lat.toFixed(7), +ll.lng.toFixed(7));
                    await ctx.drawSubgardens();
                    toast("Záhonek „" + sp.name + "“ propojen a umístěn na mapě");
                    void openSubgardens(ctx);
                  } catch (e) {
                    toast(e instanceof Error ? e.message : "Propojení se nezdařilo.");
                  }
                }, () => void openSubgardens(ctx));
              },
            }, "Propojit a umístit")))))
    : null;

  // Quick form to create a new photo subgarden and place it on the map
  const newName = h("input", { placeholder: "např. Záhonek u skleníku", maxLength: 80 }) as HTMLInputElement;
  const fileIn = h("input", { type: "file", accept: "image/*", hidden: true }) as HTMLInputElement;
  const createBtn = h("button", {
    type: "button",
    class: "btn primary block",
    html: I.plus + "<span>Vybrat fotku a umístit nový záhonek na mapu</span>",
    onclick: () => {
      if (!newName.value.trim()) {
        newName.focus();
        toast("Zadej nejprve název nového záhonku.");
        return;
      }
      fileIn.click();
    },
  });

  fileIn.onchange = () => {
    const f = fileIn.files?.[0];
    fileIn.value = "";
    if (!f) return;
    const subName = newName.value.trim() || "Záhonek";
    toast("Umísti křížek na místo nového záhonku „" + subName + "“");
    ctx.startPlacing("new", null, async (ll) => {
      try {
        const lat = +ll.lat.toFixed(7), lng = +ll.lng.toFixed(7);
        let sub: Garden = isDemo()
          ? { id: uuid(), name: subName, mode: "photo", parent_id: ctx.garden.id, parent_lat: lat, parent_lng: lng, parcels: [] }
          : (await post<{ garden: Garden }>("/api/gardens", {
              id: uuid(),
              name: subName,
              mode: "photo",
              parent_id: ctx.garden.id,
              parent_lat: lat,
              parent_lng: lng,
            })).garden;
        sub = await uploadGardenPhoto(sub, f);
        await ctx.drawSubgardens();
        toast("Záhonek „" + subName + "“ vytvořen");
        (ctx as any).cleanup();
        ctx.nav.openGarden(sub);
      } catch (e) {
        toast(e instanceof Error ? e.message : "Vytvoření záhonku se nezdařilo.");
      }
    }, () => void openSubgardens(ctx));
  };

  sheet.replaceChildren(
    head("Záhonky podle fotky", () => ctx.showSheet(null)),
    h("div", { class: "sheet-body" },
      h("p", { class: "hint" }, "Propojené záhonky se zobrazují jako značky přímo na mapě této zahrady. Klepnutím na značku se přepneš do detailní fotky záhonku."),
      listEl,
      linkExistingBox,
      editable
        ? h("div", { class: "card", style: "box-shadow:none;background:var(--surface-2);padding:14px" },
            h("b", {}, "Nový záhonek v této zahradě"),
            h("label", { class: "field" }, h("span", {}, "Název záhonku"), newName),
            createBtn,
            fileIn)
        : null));
  ctx.showSheet("subgardensSheet");
}

// In-memory shares for Demo mode so the user can test the UI without an account
const demoShares = new Map<string, GardenShare[]>();

export async function openShare(ctx: Ctx) {
  const sheet = sheetEl(ctx, "shareSheet");
  let role: "read" | "write" = "read";
  let shares: GardenShare[] = [];

  const emailIn = h("input", {
    id: "shareEmail",
    type: "email",
    inputmode: "email",
    autocomplete: "email",
    placeholder: "např. rodina@email.cz",
    required: true,
    maxLength: 200,
  }) as HTMLInputElement;
  const roleChips = h("div", { class: "chips", role: "group", "aria-label": "Oprávnění" });
  const roleHint = h("p", { class: "hint" });
  const submitBtn = h("button", { type: "submit", class: "btn primary", html: I.users + "<span>Nasdílet zahradu</span>" });
  const errEl = h("p", { class: "err", role: "alert", hidden: true });
  const listEl = h("ul", { class: "tl" });

  const drawRole = () => {
    const opts: ["read" | "write", string][] = [
      ["read", "Jen čtení"],
      ["write", "Úprava rostlin"],
    ];
    roleChips.replaceChildren(...opts.map(([k, label]) =>
      h("button", {
        type: "button",
        class: "chip",
        "aria-pressed": String(role === k),
        onclick: () => { role = k; drawRole(); },
      }, label)));
    roleHint.textContent = role === "read"
      ? "Jen čtení: uživatel si může prohlížet mapu, rostliny a fotky, ale nemůže nic měnit."
      : "Úprava rostlin: uživatel může přidávat, upravovat a mazat rostliny a jejich fotky.";
  };
  drawRole();

  const drawShares = () => {
    listEl.replaceChildren();
    if (!shares.length) {
      listEl.append(h("li", { class: "empty" }, "Zahrada zatím není s nikým sdílená."));
      return;
    }
    for (const s of shares) {
      const nextRole: "read" | "write" = s.role === "read" ? "write" : "read";
      const roleBtn = h("button", {
        type: "button",
        class: "chip",
        title: "Klepnutím přepneš oprávnění",
        onclick: () => void changeRole(s, nextRole),
      }, s.role === "write" ? "Úprava rostlin" : "Jen čtení");
      const rmBtn = h("button", {
        type: "button",
        class: "btn ghost icon-only",
        title: "Odebrat přístup",
        "aria-label": "Odebrat přístup pro " + s.email,
        onclick: () => void removeShare(s),
        html: I.trash,
      });
      listEl.append(h("li", { class: "prow" },
        h("span", { class: "t" },
          h("b", {}, s.email),
          h("small", { class: "hint" }, s.role === "write" ? "může upravovat rostliny (klepnutím na štítek změníš)" : "jen pro čtení (klepnutím na štítek změníš)")),
        roleBtn,
        rmBtn));
    }
  };

  async function loadShares() {
    if (isDemo()) {
      shares = demoShares.get(ctx.garden.id) ?? [];
      drawShares();
      return;
    }
    try {
      const r = await get<{ shares: GardenShare[] }>("/api/gardens/" + ctx.garden.id + "/shares");
      shares = r.shares;
      drawShares();
    } catch (e) {
      errEl.textContent = e instanceof Error ? e.message : "Nepodařilo se načíst sdílení.";
      errEl.hidden = false;
    }
  }

  async function changeRole(s: GardenShare, newRole: "read" | "write") {
    errEl.hidden = true;
    try {
      if (isDemo()) {
        s.role = newRole;
        drawShares();
      } else {
        const r = await post<{ share: GardenShare }>("/api/gardens/" + ctx.garden.id + "/shares", { email: s.email, role: newRole });
        shares = shares.map((x) => (x.id === s.id ? r.share : x));
        drawShares();
      }
      toast("Oprávnění upraveno: " + (newRole === "write" ? "Úprava rostlin" : "Jen čtení"));
    } catch (e) {
      errEl.textContent = e instanceof Error ? e.message : "Změna oprávnění se nezdařila.";
      errEl.hidden = false;
    }
  }

  async function removeShare(s: GardenShare) {
    errEl.hidden = true;
    try {
      if (isDemo()) {
        shares = shares.filter((x) => x.id !== s.id);
        demoShares.set(ctx.garden.id, shares);
      } else {
        await api("DELETE", "/api/gardens/" + ctx.garden.id + "/shares/" + s.id);
        shares = shares.filter((x) => x.id !== s.id);
      }
      drawShares();
      toast("Přístup pro " + s.email + " odebrán");
    } catch (e) {
      errEl.textContent = e instanceof Error ? e.message : "Odebrání přístupu se nezdařilo.";
      errEl.hidden = false;
    }
  }

  const form = h("form", { class: "card", style: "box-shadow:none;padding:0" },
    h("label", { class: "field" }, h("span", {}, "E-mail uživatele"), emailIn),
    h("div", { class: "field" }, h("span", {}, "Oprávnění"), roleChips),
    roleHint,
    errEl,
    submitBtn);

  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    const email = emailIn.value.trim().toLowerCase();
    if (!email) { emailIn.focus(); return; }
    errEl.hidden = true;
    submitBtn.setAttribute("disabled", "");
    try {
      if (isDemo()) {
        const list = demoShares.get(ctx.garden.id) ?? [];
        const ex = list.find((x) => x.email === email);
        if (ex) ex.role = role;
        else list.push({ id: uuid(), email, role, created_at: new Date().toISOString() });
        demoShares.set(ctx.garden.id, list);
        shares = list;
      } else {
        const r = await post<{ share: GardenShare }>("/api/gardens/" + ctx.garden.id + "/shares", { email, role });
        const idx = shares.findIndex((x) => x.email === r.share.email);
        if (idx >= 0) shares[idx] = r.share;
        else shares.push(r.share);
      }
      emailIn.value = "";
      drawShares();
      toast("Zahrada nasdílena pro " + email);
    } catch (e) {
      errEl.textContent = e instanceof Error ? e.message : "Sdílení se nepodařilo.";
      errEl.hidden = false;
    } finally {
      submitBtn.removeAttribute("disabled");
    }
  });

  sheet.replaceChildren(
    head("Sdílení zahrady", () => ctx.showSheet(null)),
    h("div", { class: "sheet-body" },
      form,
      h("div", { class: "field", style: "padding-top:6px" }, h("span", {}, "S kým je zahrada sdílená"), listEl),
      h("p", { class: "hint" }, "Pozvaný uživatel uvidí zahradu ve svém seznamu „Moje zahrady“ hned po přihlášení pod tímto e-mailem.")));
  ctx.showSheet("shareSheet");
  await loadShares();
}

async function openOffline(ctx: Ctx) {
  const sheet = sheetEl(ctx, "offlineSheet");
  if (!ctx.garden.parcels.length) { toast("Nejdřív přidej parcely zahrady."); return; }
  const bounds = ctx.parcelLayer.getBounds().pad(0.35);
  const urls = tileUrls(ctx.kn, bounds, 14, 20);
  const last = await store.getMeta<string | null>("offline:" + ctx.garden.id, null);
  const bar = h("i");
  const status = h("p", { class: "hint", role: "status" },
    "Stáhnu letecký snímek a katastrální mapu okolí zahrady (" + urls.length + " dlaždic, zhruba " + Math.max(1, Math.round(urls.length * 18 / 1024)) + " MB)." +
    (last ? " Naposledy uloženo " + new Date(last).toLocaleDateString("cs-CZ") + "." : ""));
  const go = h("button", { class: "btn primary block", html: I.download + "<span>Stáhnout</span>" });
  go.onclick = async () => {
    if (!navigator.onLine) { toast("Stahování potřebuje internet."); return; }
    go.setAttribute("disabled", "");
    try { await navigator.storage?.persist?.(); } catch {}
    const r = await prefetch(urls, (d, total, failed) => {
      bar.style.width = Math.round((d / total) * 100) + "%";
      status.textContent = "Staženo " + d + " z " + total + (failed ? " (" + failed + " se nepovedlo)" : "");
    });
    await store.setMeta("offline:" + ctx.garden.id, new Date().toISOString());
    status.textContent = r.failed ? "Hotovo, " + r.failed + " dlaždic se nepodařilo stáhnout. Zkus to znovu později." : "Hotovo. Mapa zahrady je uložená v telefonu.";
    go.removeAttribute("disabled");
  };
  sheet.replaceChildren(head("Mapa pro offline", () => ctx.showSheet(null)), h("div", { class: "sheet-body" },
    status, h("div", { class: "progress" }, bar), go,
    h("p", { class: "hint" }, "Rostliny a fotky se do telefonu ukládají vždy, i bez tohoto kroku. Stažení se týká jen podkladové mapy.")));
  ctx.showSheet("offlineSheet");
  void L;
}
